import { ContactMail, ContactPhone, LocationOn, PermContactCalendar } from "@mui/icons-material";
import { List, ListItem, ListItemIcon, ListItemText, Typography } from "@mui/material";
import { chooseContact } from "helpers";
import { addressToGoogleString, addressToString } from "helpers/addressToString";
import { AddressType, CustomerContactType, Maybe } from "operations/schema/schema";
import { FC, useCallback } from "react";
import { FormattedMessage, useIntl } from "react-intl";

interface ContactsProps {
  customerAddress: AddressType;
  contacts: Maybe<CustomerContactType>[];
  preferredContact?: Maybe<CustomerContactType>;
  subject: string;
}

export const ContactsMinimal: FC<ContactsProps> = (props) => {
  const { contacts, preferredContact, customerAddress, subject } = props;
  const firstContact = chooseContact(preferredContact, contacts);
  const intl = useIntl();
  const fontSize = "0.8rem";
  const addressString = addressToString(customerAddress);
  const googleString = addressToGoogleString(customerAddress);
  const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(googleString)}`;

  const openMap = useCallback(() => {
    window.open(url, "_blank", "noopener noreferrer");
  }, [url]);

  const address = (
    <ListItem
      component="a"
      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(googleString)}`}
      onClick={(e) => {
        e.preventDefault();
        openMap();
      }}
      disableGutters
      target="_blank"
      rel="noopener noreferrer"
    >
      <ListItemIcon className="min-width-28">
        <LocationOn />
      </ListItemIcon>
      <ListItemText
        disableTypography
        primary={<Typography sx={{ fontSize: fontSize }}>{addressString}</Typography>}
      />
    </ListItem>
  );

  const firstContactDetails = useCallback(
    (address: JSX.Element, firstContact: CustomerContactType) => {
      return (
        <>
          {firstContact?.name && (
            <ListItem disableGutters sx={{ fontSize: fontSize }}>
              <ListItemIcon className="min-width-28" sx={{ fontSize: "1.2rem" }}>
                <PermContactCalendar />
              </ListItemIcon>
              <ListItemText
                disableTypography
                primary={<Typography sx={{ fontSize: fontSize }}>{firstContact.name}</Typography>}
              />
            </ListItem>
          )}
          {address}
          {firstContact?.phoneNumber && (
            <ListItem component="a" href={`tel:${firstContact.phoneNumber}`} disableGutters>
              <ListItemIcon className="min-width-28" sx={{ fontSize: "1.2rem" }}>
                <ContactPhone />
              </ListItemIcon>
              <ListItemText
                disableTypography
                primary={
                  <Typography sx={{ fontSize: fontSize }}>{firstContact.phoneNumber}</Typography>
                }
              />
            </ListItem>
          )}
          {firstContact?.mobileNumber && (
            <ListItem component="a" href={`tel:${firstContact.mobileNumber}`} disableGutters>
              <ListItemIcon className="min-width-28" sx={{ fontSize: "1.2rem" }}>
                <ContactPhone />
              </ListItemIcon>
              <ListItemText
                disableTypography
                primary={
                  <Typography sx={{ fontSize: fontSize }}>{firstContact.mobileNumber}</Typography>
                }
              />
            </ListItem>
          )}
          {firstContact?.email && (
            <ListItem
              component="a"
              href={`mailto:${firstContact.email}?subject=${subject}`}
              disableGutters
            >
              <ListItemIcon className="min-width-28" sx={{ fontSize: "1.2rem" }}>
                <ContactMail />
              </ListItemIcon>
              <ListItemText
                disableTypography
                primary={<Typography sx={{ fontSize: fontSize }}>{firstContact.email}</Typography>}
              />
            </ListItem>
          )}
        </>
      );
    },
    [subject],
  );

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontSize: "1.0rem",
            fontWeight: 700,
          }}
        >
          <FormattedMessage id="contact" />
        </Typography>
      </div>
      <List dense>
        {firstContact ? (
          firstContactDetails(address, firstContact)
        ) : (
          <>
            {address}
            <br />
            <Typography variant="body2" sx={{ fontSize: fontSize }}>
              {intl.formatMessage({ id: "contact.noCustomerContacts" })}
            </Typography>
          </>
        )}
      </List>
    </>
  );
};
